import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { AdminProductListShimmer } from '../common/Shimmer.jsx';
import './AdminHealthCheck.css';

const STATUS_LABELS = {
  healthy: '正常',
  degraded: '警告',
  down: '異常',
  unconfigured: '未設定',
  pending: '未実行',
};

const ENV_LABELS = {
  prod: '本番',
  dev: '開発',
};

function formatCheckedAt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
}

function formatLatency(ms) {
  if (ms == null) return '—';
  return `${ms} ms`;
}

function formatHttpStatus(code) {
  if (code == null) return '—';
  return String(code);
}

function normalizeExternalUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
}

function groupProductHealthRows(results) {
  const map = new Map();

  for (const item of results || []) {
    if (item.category !== 'product' || !item.productId) continue;

    if (!map.has(item.productId)) {
      map.set(item.productId, {
        productId: item.productId,
        name: item.name || item.productId,
        displayOrder: item.displayOrder ?? 9999,
        isActive: item.isActive,
        isComingSoon: item.isComingSoon,
        prod: null,
        dev: null,
      });
    }

    const group = map.get(item.productId);
    if (item.environment === 'prod') group.prod = item;
    if (item.environment === 'dev') group.dev = item;
    if (item.name) group.name = item.name;
    if (item.displayOrder != null) group.displayOrder = item.displayOrder;
    if (item.isActive != null) group.isActive = item.isActive;
    if (item.isComingSoon != null) group.isComingSoon = item.isComingSoon;
  }

  return [...map.values()].sort((a, b) => {
    const orderCmp = (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999);
    if (orderCmp !== 0) return orderCmp;
    return (a.name || '').localeCompare(b.name || '', 'ja');
  });
}

function worstStatus(prodItem, devItem) {
  const order = { down: 0, degraded: 1, unconfigured: 2, pending: 3, healthy: 4 };
  const statuses = [prodItem?.status, devItem?.status].filter(Boolean);
  if (!statuses.length) return 'pending';
  return statuses.reduce((worst, s) => (
    (order[s] ?? 99) < (order[worst] ?? 99) ? s : worst
  ), statuses[0]);
}

const EnvHealthPill = ({ env, item }) => {
  if (!item) {
    return (
      <span className="dashboard-product-env-health dashboard-product-env-health--empty">
        <span className="dashboard-product-env-label">{ENV_LABELS[env]}</span>
        <span className="dashboard-product-env-missing">—</span>
      </span>
    );
  }

  return (
    <span className="dashboard-product-env-health">
      <span className="dashboard-product-env-label">{ENV_LABELS[env]}</span>
      <span className={`health-status-badge mini ${item.status}`}>
        {STATUS_LABELS[item.status] || item.status}
      </span>
      {item.latencyMs != null && (
        <span className="dashboard-product-latency">{item.latencyMs} ms</span>
      )}
    </span>
  );
};

const UrlRow = ({ label, url }) => {
  const href = normalizeExternalUrl(url);
  return (
    <div className="dashboard-product-float-url-row">
      <span className="dashboard-product-float-label">{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="dashboard-product-float-link mono">
          {url.trim()}
        </a>
      ) : (
        <span className="dashboard-product-float-muted">未設定</span>
      )}
    </div>
  );
};

const ComponentMiniList = ({ title, layer }) => {
  if (!layer?.components?.length) return null;
  return (
    <div className="dashboard-product-float-components">
      <div className="dashboard-product-float-components-title">{title}</div>
      <ul className="dashboard-product-float-components-list">
        {layer.components.map((comp) => (
          <li key={comp.id}>
            <span className={`health-status-badge mini ${comp.status}`}>
              {STATUS_LABELS[comp.status] || comp.status}
            </span>
            <span className="dashboard-product-float-comp-id">{comp.id}</span>
            <span className="dashboard-product-float-comp-meta">
              {formatLatency(comp.latencyMs)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const EnvDetailBlock = ({ env, item }) => (
  <div className="dashboard-product-float-env-block">
    <div className="dashboard-product-float-env-head">
      <h4>{ENV_LABELS[env]}</h4>
      {item ? (
        <span className={`health-status-badge ${item.status}`}>
          {STATUS_LABELS[item.status] || item.status}
        </span>
      ) : (
        <span className="dashboard-product-float-muted">データなし</span>
      )}
    </div>

    {item && (
      <>
        <UrlRow label="起動 URL" url={item.sourceUrl} />
        <UrlRow
          label="L1 チェック URL"
          url={item.liveness?.checkedUrl || item.checkedUrl}
        />

        <div className="dashboard-product-float-metrics">
          <div>
            <span className="dashboard-product-float-label">HTTP</span>
            <span>{formatHttpStatus(item.httpStatus ?? item.liveness?.httpStatus)}</span>
          </div>
          <div>
            <span className="dashboard-product-float-label">レイテンシ</span>
            <span>{formatLatency(item.latencyMs ?? item.liveness?.latencyMs)}</span>
          </div>
          <div>
            <span className="dashboard-product-float-label">最終確認</span>
            <span>{formatCheckedAt(item.checkedAt)}</span>
          </div>
          <div>
            <span className="dashboard-product-float-label">L2</span>
            <span>{formatCheckedAt(item.detailed?.checkedAt)}</span>
          </div>
          <div>
            <span className="dashboard-product-float-label">L3</span>
            <span>{formatCheckedAt(item.smoke?.checkedAt)}</span>
          </div>
        </div>

        {item.error && (
          <div className="dashboard-product-float-error">
            <span className="dashboard-product-float-label">エラー</span>
            <p>{item.error}</p>
          </div>
        )}

        <ComponentMiniList title="L2 コンポーネント" layer={item.detailed} />
        <ComponentMiniList title="L3 コンポーネント" layer={item.smoke} />
      </>
    )}
  </div>
);

const DashboardProductDetailFloat = ({ product, onClose }) => {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  if (!product) return null;

  return (
    <div
      className="dashboard-product-float-overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="dashboard-product-float"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-product-float-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dashboard-product-float-header">
          <div>
            <h3 id="dashboard-product-float-title">{product.name}</h3>
            <p className="dashboard-product-float-id mono">{product.productId}</p>
            <div className="dashboard-product-meta">
              {product.isComingSoon && (
                <span className="dashboard-product-tag coming-soon">Coming Soon</span>
              )}
              {product.isActive === false && (
                <span className="dashboard-product-tag private">非公開</span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="dashboard-product-float-close"
            onClick={onClose}
            aria-label="閉じる"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="dashboard-product-float-body">
          <EnvDetailBlock env="prod" item={product.prod} />
          <EnvDetailBlock env="dev" item={product.dev} />
        </div>
      </div>
    </div>
  );
};

/**
 * ダッシュボード用プロダクト一覧（本番・開発のヘルスをコンパクト表示）
 */
const DashboardProductHealthList = ({
  report,
  loading,
  onOpenHealthCheck,
  onOpenProducts,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  const products = useMemo(
    () => groupProductHealthRows(report?.results),
    [report?.results]
  );

  const closeFloat = useCallback(() => setSelectedProduct(null), []);

  if (loading && !report) {
    return <AdminProductListShimmer cards={4} />;
  }

  if (!products.length) {
    return (
      <section className="dashboard-products-section">
        <div className="dashboard-products-header">
          <div>
            <h3>プロダクト</h3>
            <p className="dashboard-products-subtitle">登録プロダクトがありません</p>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenProducts}>
            プロダクト管理
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="dashboard-products-section">
        <div className="dashboard-products-header">
          <div>
            <h3>プロダクト</h3>
            <p className="dashboard-products-subtitle">
              {products.length} 件 — カードをクリックで URL・ヘルス詳細を表示
              {report?.source === 'catalog-only' ? '（初回チェック待ち）' : ''}
            </p>
          </div>
          <div className="dashboard-products-header-actions">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenHealthCheck}>
              ヘルスチェック詳細
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onOpenProducts}>
              プロダクト管理
            </button>
          </div>
        </div>

        <ul className="dashboard-product-list">
          {products.map((product) => {
            const overall = worstStatus(product.prod, product.dev);
            return (
              <li key={product.productId}>
                <button
                  type="button"
                  className={`dashboard-product-card status-${overall}`}
                  onClick={() => setSelectedProduct(product)}
                  aria-haspopup="dialog"
                >
                  <div className="dashboard-product-card-main">
                    <span className="dashboard-product-name">{product.name}</span>
                    <div className="dashboard-product-meta">
                      {product.isComingSoon && (
                        <span className="dashboard-product-tag coming-soon">Coming Soon</span>
                      )}
                      {product.isActive === false && (
                        <span className="dashboard-product-tag private">非公開</span>
                      )}
                    </div>
                  </div>
                  <div className="dashboard-product-health-row">
                    <EnvHealthPill env="prod" item={product.prod} />
                    <EnvHealthPill env="dev" item={product.dev} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <DashboardProductDetailFloat product={selectedProduct} onClose={closeFloat} />
    </>
  );
};

export default DashboardProductHealthList;
